import type { ResumeDocument } from "@/types/cv";

// Shared Indonesian sample CV so public marketing previews and template
// downloads show a realistic, ATS-safe output with the same underlying content.
export const CV_BUILDER_EXAMPLE = {
  name: "John Doe",
  email: "john.doe@gmail.com",
  phoneNumber: "+62 812 9012 8841",
  city: "Jakarta",
  linkedin: "linkedin.com/in/johndoe",
  summary:
    "Fresh graduate Ilmu Komunikasi dengan pengalaman magang di CRM, campaign execution, dan analytics untuk produk edukasi digital. Terbiasa mengolah performa campaign jadi insight yang bisa langsung dipakai tim marketing untuk menaikkan lead berkualitas dan conversion. Mencari peran Marketing Associate atau CRM Associate untuk membantu brand tumbuh lewat eksekusi yang rapi dan keputusan berbasis data.",
  experienceTitle: "CRM & Digital Marketing Intern",
  experienceCompany: "PT MajuLangkah Edukasi",
  experienceBullet:
    "Susun 9 campaign email onboarding dan win-back, dorong open rate 32% dan conversion webinar ke trial 18% dalam 3 bulan.",
  institution: "Universitas Indonesia",
  degree: "S1",
  major: "Ilmu Komunikasi",
  gpa: "3.84 / 4.00",
  technicalSkills:
    "Google Analytics 4, Looker Studio, HubSpot, Meta Ads, CRM, A/B Testing",
  softSkills:
    "Komunikasi, project management, problem solving, kolaborasi lintas tim",
  language: "English",
  languageLevel: "Professional working proficiency",
} as const;

export const SAMPLE_RESUME: ResumeDocument = {
  personal_identity: {
    name: CV_BUILDER_EXAMPLE.name,
    email: CV_BUILDER_EXAMPLE.email,
    phone_number: CV_BUILDER_EXAMPLE.phoneNumber,
    location: {
      city: CV_BUILDER_EXAMPLE.city,
      country: "Indonesia",
      relocation_open: true,
    },
    linkedin: CV_BUILDER_EXAMPLE.linkedin,
    additional_links: [
      { label: "Portfolio", url: "johndoe.framer.website" },
      { label: "LinkedIn", url: "linkedin.com/in/johndoe" },
    ],
  },
  professional_summary: CV_BUILDER_EXAMPLE.summary,
  working_experience: [
    {
      title: "CRM & Digital Marketing Intern",
      rewrite_title: "CRM & Digital Marketing Intern",
      company: "PT MajuLangkah Edukasi",
      location: "Jakarta",
      employment_type: "Magang",
      start_date: "2024-02",
      end_date: "2024-08",
      is_current: false,
      experience_detail:
        "Mendukung tim CRM dan performance marketing untuk campaign acquisition, onboarding, dan re-engagement calon pengguna.",
      points: [
        "Susun 9 campaign email onboarding dan win-back, dorong open rate 32% dan conversion webinar ke trial 18% dalam 3 bulan",
        "Buat dashboard campaign mingguan di Looker Studio, pangkas waktu reporting tim dari 4 jam jadi 45 menit",
        "Analisis performa audience dan creative Meta Ads, bantu turunkan cost per lead 16% tanpa menurunkan volume registrasi",
      ],
    },
    {
      title: "Marketing Project Intern",
      rewrite_title: "Marketing Project Intern",
      company: "KitaBisa Career Hub",
      location: "Jakarta",
      employment_type: "Magang",
      start_date: "2023-07",
      end_date: "2023-12",
      is_current: false,
      experience_detail:
        "Membantu eksekusi webinar karier, konten promosi, dan kebutuhan reporting untuk funnel registrasi peserta.",
      points: [
        "Koordinasi distribusi konten dan landing page untuk 5 webinar karier, hasilkan total 1.400+ registran",
        "Tulis copy promosi email dan sosial media yang menaikkan CTR rata-rata 21% dibanding batch sebelumnya",
        "Kolaborasi dengan desain dan partnership untuk materi sponsor, bantu capai 100% target dukungan acara",
      ],
    },
  ],
  organizations: [
    {
      title: "Koordinator Acara",
      rewrite_title: "Koordinator Acara",
      org: "BEM FISIP Universitas Indonesia",
      location: "Depok",
      start_date: "2022-08",
      end_date: "2023-08",
      is_current: false,
      experience_detail:
        "Memimpin perencanaan seminar karier dan koordinasi lintas divisi untuk event pengembangan mahasiswa.",
      points: [
        "Koordinasi 16 panitia untuk seminar karier dengan 600+ peserta dari 12 kampus",
        "Kelola rundown, vendor, dan kebutuhan pembicara sehingga acara berjalan tepat waktu dan sesuai anggaran",
      ],
    },
  ],
  education: [
    {
      institution: CV_BUILDER_EXAMPLE.institution,
      degree: CV_BUILDER_EXAMPLE.degree,
      major: CV_BUILDER_EXAMPLE.major,
      program_title: "S1 Ilmu Komunikasi",
      rewrite_program_title: "S1 Ilmu Komunikasi",
      start_date: "2020-08",
      end_date: "2024-05",
      gpa: CV_BUILDER_EXAMPLE.gpa,
      points: [
        "Lulus cum laude. Fokus riset pada perilaku konsumen digital dan efektivitas campaign media sosial.",
      ],
    },
  ],
  additional_information: {
    tools: [
      "Google Analytics 4",
      "Looker Studio",
      "HubSpot",
      "Meta Ads Manager",
      "Canva",
      "Google Sheets",
    ],
    technical_skills: [
      "Campaign Analytics",
      "CRM Campaign",
      "Audience Segmentation",
      "A/B Testing",
      "Copywriting",
      "Landing Page Optimization",
    ],
    soft_skills: [
      "Komunikasi",
      "Attention to Detail",
      "Project Management",
      "Cross-functional Collaboration",
    ],
    languages: [
      { name: "Bahasa Indonesia", proficiency: "Native" },
      { name: "English", proficiency: CV_BUILDER_EXAMPLE.languageLevel },
    ],
    certificates_awards: [
      {
        title: "Google Analytics Certification",
        rewrite_title: "Google Analytics Certification",
        issuer: "Google",
        date: "2025-03",
        description:
          "Sertifikasi pengukuran performa digital, event tracking, dan evaluasi campaign.",
        url: "",
      },
      {
        title: "Fundamentals of Digital Marketing",
        rewrite_title: "Fundamentals of Digital Marketing",
        issuer: "Google Digital Garage",
        date: "2024-09",
        description:
          "Sertifikasi dasar strategi digital marketing, content promotion, dan customer journey.",
        url: "",
      },
    ],
  },
};
